import { useEffect, useMemo, useState } from "react";
import AppLayout from "@/components/AppLayout";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import MaterialFile from "@/components/MaterialFile";
import { youtubeEmbedUrl } from "@/lib/youtube";
import { Video as VideoIcon, ExternalLink } from "lucide-react";

export default function AthleteVideos() {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeCat, setActiveCat] = useState("Todos");

  // Bootstrap único no mount — intencional.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    (async () => {
      setLoading(true); setError(null);
      try {
        const [personal, library] = await Promise.all([api.get("/my-videos"), api.get("/videos")]);
        setVideos(Array.from(new Map([...personal.data, ...library.data].map((video) => [video.id, video])).values()));
      } catch (err) {
        console.error("[videos] falha ao carregar /my-videos:", err);
        setError("Não foi possível carregar seus materiais.");
      }
      finally { setLoading(false); }
    })();
  }, []);

  const categories = useMemo(() => {
    const set = new Set(videos.map(v => v.category || "Outros"));
    return ["Todos", ...Array.from(set)];
  }, [videos]);

  const grouped = useMemo(() => {
    const filtered = activeCat === "Todos" ? videos : videos.filter(v => (v.category||"Outros") === activeCat);
    const map = new Map();
    for (const v of filtered) {
      const c = v.category || "Outros";
      if (!map.has(c)) map.set(c, []);
      map.get(c).push(v);
    }
    return Array.from(map.entries());
  }, [videos, activeCat]);

  return (
    <AppLayout kind="athlete">
      <h1 className="text-3xl sm:text-4xl text-white font-semibold mb-2">Meus Materiais</h1>
      <p className="text-slate-400 mb-6">Vídeos, apostilas e PDFs da biblioteca, além dos vídeos selecionados pelo seu treinador para você.</p>

      {!loading && !error && !!videos.length && (
        <div className="flex flex-wrap gap-2 mb-6" data-testid="video-filter-tabs">
          {categories.map(c => (
            <button key={c} onClick={()=>setActiveCat(c)} data-testid={`video-filter-${c}`}
              className={`px-3 py-1.5 rounded-full text-xs uppercase tracking-widest transition border ${
                activeCat === c
                  ? "bg-amber-500 text-[#071A33] border-amber-500 font-semibold"
                  : "bg-transparent text-slate-300 border-white/10 hover:bg-white/5"
              }`}>{c}</button>
          ))}
        </div>
      )}

      {loading && <div className="text-slate-400 py-10 text-center" data-testid="videos-loading">Carregando…</div>}
      {error && !loading && <div className="text-red-300 py-4">{error}</div>}
      {!loading && !error && !videos.length && (
        <Card className="bg-transparent gsx-card-dark p-10 text-center" data-testid="videos-empty">
          <VideoIcon className="w-10 h-10 mx-auto text-amber-400/70" />
          <div className="text-white font-semibold mt-4">Nenhum material disponível ainda</div>
          <p className="text-slate-400 text-sm mt-2 max-w-md mx-auto">Quando seu treinador adicionar materiais, eles aparecerão aqui.</p>
        </Card>
      )}

      {!loading && !!videos.length && grouped.map(([cat, list]) => (
        <section key={cat} className="mb-8" data-testid={`video-section-${cat}`}>
          <h2 className="text-white font-semibold mb-3 flex items-center gap-2">
            <span className="text-xs uppercase tracking-widest text-amber-300">{cat}</span>
            <span className="text-xs text-slate-500">({list.length})</span>
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {list.map(v => (
              <Card key={v.id} className="bg-transparent gsx-card-dark overflow-hidden" data-testid={`athlete-video-${v.id}`}>
                {!v.file_id && <div className="aspect-video bg-black">
                  {youtubeEmbedUrl(v.youtube_url) ? (
                    <iframe
                      src={youtubeEmbedUrl(v.youtube_url)}
                      title={v.title}
                      className="w-full h-full"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      referrerPolicy="strict-origin-when-cross-origin"
                      allowFullScreen
                    />
                  ) : (
                    <div className="h-full flex items-center justify-center p-4 text-center text-slate-400 text-sm">
                      URL do YouTube indisponível para reprodução.
                    </div>
                  )}
                </div>}
                <div className="p-4">
                  <h3 className="text-white font-semibold">{v.title}</h3>
                  {v.description && <p className="text-xs text-slate-400 mt-2 line-clamp-3">{v.description}</p>}
                  {v.file_id ? <MaterialFile material={v} /> : <a href={v.youtube_url} target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-1 mt-3 text-amber-400 text-sm hover:text-amber-300">
                    <ExternalLink className="w-3.5 h-3.5" /> Abrir no YouTube
                  </a>}
                </div>
              </Card>
            ))}
          </div>
        </section>
      ))}
    </AppLayout>
  );
}
